import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TemporaryChatContext } from '../Context/temporaryChatContext';
import chatService from '../services/chatService';
import sessionsService from '../services/sessionsServices';

function parsePossiblyBrokenJson(data) {
  if (typeof data === 'object') return data;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

function extractJsonObjects(buffer) {
  const objects = [];
  let depth = 0;
  let start = 0;

  for (let i = 0; i < buffer.length; i++) {
    if (buffer[i] === '{') {
      if (depth === 0) start = i;
      depth++;
    } else if (buffer[i] === '}') {
      depth--;
      if (depth === 0) {
        objects.push(buffer.slice(start, i + 1));
      }
    }
  }

  const last = objects.length ? buffer.slice(buffer.lastIndexOf('}') + 1) : buffer;
  return { objects, leftover: last };
}

async function processStream(reader, handlers) {
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const { objects, leftover } = extractJsonObjects(buffer);

    for (const jsonString of objects) {
      try {
        const parsed = JSON.parse(jsonString);
        const { type, data } = parsed;

        if (type === 'intermediate_step') {
          handlers.updateChatLoaderMessage?.(data);
          handlers.updateBotChat?.('\n');
        }
        if (type === 'content') handlers.updateBotChat?.(data);
        if (type === 'tool_image_result') handlers.updateToolImages?.(data);
        if (type === 'tool_result') {
          const fixed = parsePossiblyBrokenJson(data);
          handlers.updateChatSource?.(fixed?.search_results);
        }
      } catch (err) {
        console.warn('Failed to parse chunk:', jsonString, err);
      }
    }

    buffer = leftover;
  }

  if (buffer.trim()) {
    try {
      const parsed = JSON.parse(buffer);
      const { type, data } = parsed;
      if (type === 'intermediate_step') handlers.updateChatLoaderMessage?.(data);
      if (type === 'content') handlers.updateBotChat?.(data);
      if (type === 'tool_image_result') handlers.updateToolImages?.(data);
      if (type === 'tool_result') {
        const fixed = parsePossiblyBrokenJson(data);
        handlers.updateChatSource?.(fixed?.search_results);
      }
    } catch (err) {
      console.warn('Failed final parse:', buffer, err);
    }
  }

  handlers.updateChatLoaderMessage?.('');
}

export function useChatSubmit({ createSession = false, sessionId } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    updateUserChat,
    webSearch,
    updateBotChat,
    setChatmessages,
    setSessionID,
    updateChatSource,
    selectedAgent,
    toggleRef,
    fakeAiLoader,
    fakeAiLoaderClear,
    updateChatLoaderMessage,
    updateToolImages,
  } = useContext(TemporaryChatContext);

  const mutationCreateSession = useMutation({
    mutationFn: () =>
      sessionsService.createSession({
        platform_id: 'dugout_labs',
        agent_id: selectedAgent?.value,
        project_id: 'test',
        mode: selectedAgent?.mode,
      }),
  });

  const streamHandlers = {
    updateChatLoaderMessage,
    updateBotChat,
    updateToolImages,
    updateChatSource,
  };

  const submitMessage = async text => {
    const query = text?.trim();
    if (!query || isSubmitting) return false;

    if (!createSession && !sessionId) {
      return false;
    }

    setIsSubmitting(true);
    toggleRef(false);

    try {
      if (createSession) {
        setChatmessages([]);
        const sessionRes = await mutationCreateSession.mutateAsync();
        queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });

        setSessionID(sessionRes?.session_id);
        updateUserChat(query);
        navigate(`/chat/${sessionRes?.session_id}`);

        fakeAiLoader();
        chatService.generateTitle({
          session_id: sessionRes?.session_id,
          query,
        });

        const aiResp = await chatService.createChat({
          platform_id: 'dugout_labs',
          agent_id: selectedAgent?.value,
          session_id: sessionRes?.session_id,
          mode: selectedAgent?.mode,
          query,
          use_google_search: webSearch,
        });

        await processStream(aiResp.body.getReader(), streamHandlers);
      } else {
        updateUserChat(query);
        fakeAiLoader();

        const aiResp = await chatService.createChat({
          platform_id: 'dugout_labs',
          agent_id: selectedAgent?.value,
          session_id: sessionId,
          query,
          mode: selectedAgent?.mode,
          use_google_search: webSearch,
        });

        await processStream(aiResp.body.getReader(), streamHandlers);
      }

      return true;
    } catch (error) {
      fakeAiLoaderClear();
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitMessage, isSubmitting };
}
